import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Boxes, FolderTree, Upload, ClipboardPlus, PackageSearch } from "lucide-react";
import { inventoryApi } from "../api";
import { InventoryPageHeader } from "../components/InventoryPageHeader";
import { InventoryPagination } from "../components/InventoryPagination";
import { InventoryError, InventoryLoading } from "../components/InventoryState";
import { useAsyncData } from "../hooks";
import type { InventoryItem, PageResponse } from "../types";

export default function InventoryDashboardPage() {
  const [lowStockPage, setLowStockPage] = useState(0);
  const [lowStockSize, setLowStockSize] = useState(10);

  const { data: stats, loading, error } = useAsyncData(async () => {
    const items = await inventoryApi.listItems({ page: 0, size: 100 });
    const lowStockItems = items.content.filter((i) => i.belowReorderLevel);
    return {
      totalItems: items.totalElements,
      lowStockAlerts: lowStockItems.length,
      lowStockList: lowStockItems,
    };
  }, []);

  const lowStockTotalPages = stats ? Math.max(1, Math.ceil(stats.lowStockList.length / lowStockSize)) : 1;
  const lowStockPageItems = stats
    ? stats.lowStockList.slice(lowStockPage * lowStockSize, lowStockPage * lowStockSize + lowStockSize)
    : [];
  const lowStockPageResponse: PageResponse<InventoryItem> | undefined = stats
    ? {
        content: lowStockPageItems,
        totalElements: stats.lowStockList.length,
        totalPages: lowStockTotalPages,
        number: lowStockPage,
        size: lowStockSize,
        first: lowStockPage === 0,
        last: lowStockPage + 1 >= lowStockTotalPages,
      }
    : undefined;

  return (
    <div className="space-y-6">
      <InventoryPageHeader
        title="Inventory Dashboard"
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/hr/requisitions/new" className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
              New Request
            </Link>
          </div>
        }
      />

      {loading && <InventoryLoading label="Loading dashboard..." />}
      {error && <InventoryError message={error} />}

      {!loading && !error && stats && (
        <>
          {/* Stat cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard label="Total Items" value={stats.totalItems} icon={Boxes} tone="indigo" />
            <StatCard
              label="Low Stock Alerts"
              value={stats.lowStockAlerts}
              icon={AlertTriangle}
              tone={stats.lowStockAlerts > 0 ? "red" : "emerald"}
            />
          </div>

          {/* Quick actions */}
          <div>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Quick Actions</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <QuickLink to="/hr/requisitions/new" title="New Request" desc="Submit a purchase request" icon={ClipboardPlus} tone="emerald" />
              <QuickLink to="/inventory/items" title="Items" desc="Browse inventory item master" icon={PackageSearch} tone="blue" />
              <QuickLink to="/inventory/categories" title="Categories" desc="Manage item categories" icon={FolderTree} tone="amber" />
              <QuickLink to="/inventory/import" title="Import" desc="Upload stock and item files" icon={Upload} tone="violet" />
            </div>
          </div>

          {/* Low Stock */}
          {stats.lowStockList.length > 0 && (
            <div className="rounded-2xl border bg-white shadow-sm">
              <div className="border-b px-5 py-4">
                <h2 className="font-bold text-slate-900">Low Stock Items</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Item</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Issue Stock</th>
                      <th className="px-4 py-3">Reorder Level</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockPageItems.map((item, idx) => (
                      <tr
                        key={item.id}
                        className={`border-l-2 border-l-amber-400 hover:bg-amber-50 ${idx % 2 === 1 ? "bg-slate-50/70" : "bg-white"}`}
                      >
                        <td className="px-4 py-3 font-semibold text-slate-900">{item.code}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                        <td className="px-4 py-3 text-slate-500">{item.categoryName || "-"}</td>
                        <td className="px-4 py-3 font-semibold text-amber-700">
                          {Number(item.currentStockUnits || 0).toLocaleString()} {item.issueUnitName}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {item.reorderLevelInIssueUnits != null
                            ? `${item.reorderLevelInIssueUnits} ${item.issueUnitName}`
                            : <span className="italic text-slate-400">Not set</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/inventory/items/${item.id}`} className="font-semibold text-emerald-700 hover:underline">
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t px-5 py-3">
                <InventoryPagination
                  page={lowStockPage}
                  setPage={setLowStockPage}
                  size={lowStockSize}
                  setSize={(s) => { setLowStockSize(s); setLowStockPage(0); }}
                  data={lowStockPageResponse}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

type Tone = "indigo" | "emerald" | "blue" | "amber" | "red" | "violet" | "slate";

const TONE_STYLES: Record<Tone, string> = {
  indigo: "bg-indigo-50 border-indigo-100 text-indigo-700",
  emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
  blue: "bg-blue-50 border-blue-100 text-blue-700",
  amber: "bg-amber-50 border-amber-100 text-amber-700",
  red: "bg-red-50 border-red-100 text-red-700",
  violet: "bg-violet-50 border-violet-100 text-violet-700",
  slate: "bg-slate-50 border-slate-100 text-slate-600",
};

function StatCard({
  label, value, icon: Icon, tone,
}: {
  label: string; value?: number | null; icon: React.ComponentType<{ className?: string }>; tone: Tone;
}) {
  return (
    <div className={`rounded-2xl border p-5 shadow-sm transition-shadow hover:shadow-md ${TONE_STYLES[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
        <Icon className="h-5 w-5 opacity-50" />
      </div>
      <div className="mt-3 text-3xl font-bold">{value != null ? value.toLocaleString() : "\u2014"}</div>
    </div>
  );
}

function QuickLink({
  to, title, desc, icon: Icon, tone,
}: {
  to: string; title: string; desc: string; icon: React.ComponentType<{ className?: string }>; tone: Tone;
}) {
  return (
    <Link
      to={to}
      className={`rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${TONE_STYLES[tone]}`}
    >
      <Icon className="h-5 w-5 opacity-60" />
      <div className="mt-2 font-bold text-slate-900">{title}</div>
      <p className="mt-1 text-sm text-slate-500">{desc}</p>
    </Link>
  );
}
